import { useCallback, useEffect, useRef, useState } from 'react';
import {
	computeColSpanFromResizePointer,
	getPersistedChartColSpanCycle,
	resolvePersistedChartResizeConstraints,
} from '../lib/statisticsPersistedCharts';
import { attachDashboardDragAutoScroll } from '../../../../../utils/dashboardDragAutoScroll';
import {
	DASHBOARD_DRAG_APPEND_TARGET_ID,
	resolveInsertBeforeTargetId,
} from '../../../../../utils/dashboardDragReorder';

const EMPTY_DRAG_IMAGE =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const useDashboardChartsGridInteractions = ({
	gridRef,
	gridColumns,
	chartCount = 0,
	orderedIds = [],
	onReorder,
	onResizeColSpan,
	getColSpanCycle = getPersistedChartColSpanCycle,
	draggingBodyClass = 'statistics-persisted-charts--dragging',
	cardSelector = '.statistics-persisted-chart-card',
	createDragGhost,
}) => {
	const canResizeWidth = gridColumns > 1 && typeof onResizeColSpan === 'function';
	const canReorder = chartCount > 1 && typeof onReorder === 'function';
	const dragGhostRef = useRef(null);
	const draggingIdRef = useRef(null);
	const [draggingId, setDraggingId] = useState(null);
	const [dragOverId, setDragOverId] = useState(null);
	const [resizingColSpan, setResizingColSpan] = useState(null);

	const clearDragState = useCallback(() => {
		if (dragGhostRef.current) {
			dragGhostRef.current.remove();
			dragGhostRef.current = null;
		}
		draggingIdRef.current = null;
		setDraggingId(null);
		setDragOverId(null);
	}, []);

	useEffect(() => {
		if (!draggingId) return undefined;

		document.body.classList.add(draggingBodyClass);

		const detachAutoScroll = attachDashboardDragAutoScroll({
			getRootEl: () => gridRef.current,
			isDraggingRef: draggingIdRef,
		});

		const onDocumentDragEnd = () => {
			clearDragState();
		};
		document.addEventListener('dragend', onDocumentDragEnd);

		return () => {
			document.body.classList.remove(draggingBodyClass);
			detachAutoScroll();
			document.removeEventListener('dragend', onDocumentDragEnd);
		};
	}, [clearDragState, draggingBodyClass, draggingId, gridRef]);

	useEffect(() => {
		if (!resizingColSpan) return undefined;

		document.body.classList.add('statistics-persisted-charts--resizing');
		return () => {
			document.body.classList.remove('statistics-persisted-charts--resizing');
		};
	}, [resizingColSpan]);

	const handleDragStart = useCallback(
		(event, chartId) => {
			event.stopPropagation();
			draggingIdRef.current = chartId;
			event.dataTransfer.effectAllowed = 'move';
			event.dataTransfer.setData('text/plain', chartId);

			const item = event.currentTarget.closest('.statistics-persisted-charts__item');
			const card = item?.querySelector(cardSelector);
			if (card && createDragGhost) {
				const { host, offsetX, offsetY } = createDragGhost(card, event);
				dragGhostRef.current = host;
				event.dataTransfer.setDragImage(host, offsetX, offsetY);
			} else {
				const emptyImage = new Image();
				emptyImage.src = EMPTY_DRAG_IMAGE;
				event.dataTransfer.setDragImage(emptyImage, 0, 0);
			}

			setDraggingId(chartId);
			setDragOverId(null);
		},
		[cardSelector, createDragGhost],
	);

	const handleDragEnd = useCallback(() => {
		clearDragState();
	}, [clearDragState]);

	const handleDragOver = useCallback(
		(event, chartId) => {
			const sourceId = draggingIdRef.current;
			if (!sourceId) return;
			event.preventDefault();
			event.stopPropagation();
			event.dataTransfer.dropEffect = 'move';

			const targetId = resolveInsertBeforeTargetId(orderedIds, sourceId, chartId, event.clientY, event.currentTarget.getBoundingClientRect());
			if (!targetId || targetId === sourceId) return;
			setDragOverId(targetId);
		},
		[orderedIds],
	);

	const handleAppendDragOver = useCallback((event) => {
		const sourceId = draggingIdRef.current;
		if (!sourceId) return;
		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer.dropEffect = 'move';
		setDragOverId(DASHBOARD_DRAG_APPEND_TARGET_ID);
	}, []);

	const handleAppendDrop = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();
			const sourceId = event.dataTransfer.getData('text/plain') || draggingIdRef.current;
			if (sourceId) {
				onReorder?.(sourceId, DASHBOARD_DRAG_APPEND_TARGET_ID);
			}
			clearDragState();
		},
		[clearDragState, onReorder],
	);

	const handleItemDragLeave = useCallback((event, chartId) => {
		const related = event.relatedTarget;
		if (related instanceof Node && event.currentTarget.contains(related)) return;
		setDragOverId((current) => (current === chartId ? null : current));
	}, []);

	const handleGridDragLeave = useCallback((event) => {
		const related = event.relatedTarget;
		if (related instanceof Node && gridRef.current?.contains(related)) return;
		setDragOverId(null);
	}, [gridRef]);

	const handleDrop = useCallback(
		(event, targetId) => {
			event.preventDefault();
			event.stopPropagation();
			const sourceId = event.dataTransfer.getData('text/plain') || draggingIdRef.current;
			if (sourceId && targetId && (targetId === DASHBOARD_DRAG_APPEND_TARGET_ID || sourceId !== targetId)) {
				onReorder?.(sourceId, targetId);
			}
			clearDragState();
		},
		[clearDragState, onReorder],
	);

	const handleCycleColSpan = useCallback(
		(chartId, currentColSpan) => {
			if (!canResizeWidth) return;
			const nextColSpan = getColSpanCycle(currentColSpan, gridColumns);
			onResizeColSpan?.(chartId, nextColSpan, gridColumns);
		},
		[canResizeWidth, getColSpanCycle, gridColumns, onResizeColSpan],
	);

	const handleResizePointerDown = useCallback(
		(event, chartId) => {
			if (!canResizeWidth) return;
			event.preventDefault();
			event.stopPropagation();

			const gridEl = gridRef.current;
			const itemEl = event.currentTarget.closest('.statistics-persisted-charts__item');
			if (!gridEl || !itemEl) return;

			const resizeConstraints = resolvePersistedChartResizeConstraints({
				itemEl,
				gridEl,
				maxColumns: gridColumns,
			});

			const updatePreview = (clientX) => {
				const colSpan = computeColSpanFromResizePointer({
					clientX,
					...resizeConstraints,
					maxColumns: gridColumns,
				});
				setResizingColSpan({ chartId, colSpan });
			};

			updatePreview(event.clientX);

			const onMove = (moveEvent) => {
				updatePreview(moveEvent.clientX);
			};

			const onUp = (upEvent) => {
				const colSpan = computeColSpanFromResizePointer({
					clientX: upEvent.clientX,
					...resizeConstraints,
					maxColumns: gridColumns,
				});
				onResizeColSpan?.(chartId, colSpan, gridColumns);
				setResizingColSpan(null);
				document.removeEventListener('pointermove', onMove);
				document.removeEventListener('pointerup', onUp);
			};

			document.addEventListener('pointermove', onMove);
			document.addEventListener('pointerup', onUp);
		},
		[canResizeWidth, gridColumns, gridRef, onResizeColSpan],
	);

	const resolveDisplayColSpan = useCallback(
		(chartId, savedColSpan) => (resizingColSpan?.chartId === chartId ? resizingColSpan.colSpan : savedColSpan),
		[resizingColSpan],
	);

	return {
		canResizeWidth,
		canReorder,
		draggingId,
		dragOverId,
		resizingColSpan,
		handleDragStart,
		handleDragEnd,
		handleDragOver,
		handleAppendDragOver,
		handleAppendDrop,
		handleItemDragLeave,
		handleGridDragLeave,
		handleDrop,
		handleCycleColSpan,
		handleResizePointerDown,
		resolveDisplayColSpan,
	};
};

export default useDashboardChartsGridInteractions;
